import { useEffect, useState, type ReactNode } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Callout, Dialog, Flex, Select, Text, TextArea, TextField } from '@radix-ui/themes'
import { Ops } from '@willie/schemas'
import { FieldShell } from '~/components/FieldShell'
import { useAuth } from '~/auth/AuthProvider'
import { useTRPC } from '~/utils/trpc'

const formSchema = Ops.Freshdesk.createTicketInputSchema.omit({ installationId: true })
type FormValues = Omit<Ops.Freshdesk.CreateTicketInput, 'installationId'>

const PRIORITIES = Object.keys(Ops.Freshdesk.TICKET_PRIORITY_LABELS).map(Number)

// French: every customer-facing template is French, and this lands in the same inbox as those.
const buildSignature = (operatorName: string): string =>
  `\n\nCordialement,\n\n${operatorName}\nsupport@meetwillie.com`

interface NewTicketDialogProps {
  installationId: number
  defaultRequesterEmail?: string | null
  defaultRequesterName?: string | null
  trigger: ReactNode
}

export const NewTicketDialog = ({
  installationId,
  defaultRequesterEmail,
  defaultRequesterName,
  trigger
}: NewTicketDialogProps) => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { session } = useAuth()
  const [open, setOpen] = useState(false)

  // The signing name comes from the operator's user_profiles row, which is the only place it is
  // actually maintained — the Supabase session carries no name for an ops login.
  const employeesQuery = useQuery(
    trpc.ops.stationInstallations.listWillieEmployees.queryOptions(undefined, { enabled: open })
  )
  const operatorName =
    employeesQuery.data?.find((employee) => employee.userId === session?.user.id)?.fullName ??
    session?.user.email ??
    'L’équipe Willie'
  const signature = buildSignature(operatorName)

  const defaults: FormValues = {
    requesterEmail: defaultRequesterEmail ?? '',
    requesterName: defaultRequesterName ?? undefined,
    subject: '',
    body: signature,
    priority: 1
  }

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty }
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaults
  })

  // The name lands after the dialog is already open, so the prefill is rewritten when it arrives —
  // but never over something the operator has started typing.
  useEffect(() => {
    if (open && !isDirty) reset(defaults)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isDirty, signature])

  const createMutation = useMutation(trpc.ops.freshdesk.createTicket.mutationOptions())

  // The prefilled signature satisfies the schema's min(1) on its own, so it cannot be what guards
  // against sending the customer a sign-off with no message above it.
  const body = useWatch({ control, name: 'body' })
  const isBodyEmpty = (body ?? '').replace(signature, '').trim() === ''

  const handleOpenChange = (next: boolean) => {
    if (next) {
      createMutation.reset()
      reset(defaults)
    }
    setOpen(next)
  }

  const onSubmit = handleSubmit(async (values) => {
    try {
      // The name prefills the Freshdesk contact, so it only holds while the address is still the
      // site contact's — retyping the address makes it someone else's name.
      const requesterName = values.requesterEmail === defaultRequesterEmail ? values.requesterName : undefined
      await createMutation.mutateAsync({ ...values, requesterName, installationId })
      queryClient.invalidateQueries({
        queryKey: trpc.ops.freshdesk.listForInstallation.queryKey({ installationId })
      })
      queryClient.invalidateQueries({
        queryKey: trpc.ops.stationInstallations.events.list.queryKey({ installationId })
      })
      setOpen(false)
    } catch {
      // keep the dialog open; the mutation error renders above the footer
    }
  })

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger>{trigger}</Dialog.Trigger>
      <Dialog.Content maxWidth="560px">
        <Dialog.Title>New ticket</Dialog.Title>
        <Dialog.Description size="2" color="gray">
          Emails the customer from support@meetwillie.com and files the ticket against this installation. Their
          reply comes back to the same ticket.
        </Dialog.Description>

        <Flex direction="column" gap="3" mt="4">
          <Controller
            name="requesterEmail"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FieldShell
                label="To"
                hint={
                  error ? (
                    <Text size="1" color="red">
                      {error.message}
                    </Text>
                  ) : null
                }
              >
                <TextField.Root
                  type="email"
                  className="w-full"
                  value={field.value}
                  onBlur={field.onBlur}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              </FieldShell>
            )}
          />

          <Controller
            name="subject"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FieldShell
                label="Subject"
                hint={
                  error ? (
                    <Text size="1" color="red">
                      {error.message}
                    </Text>
                  ) : null
                }
              >
                <TextField.Root
                  className="w-full"
                  value={field.value}
                  onBlur={field.onBlur}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              </FieldShell>
            )}
          />

          <Controller
            name="priority"
            control={control}
            render={({ field }) => (
              <FieldShell label="Priority">
                <Select.Root value={String(field.value)} onValueChange={(v) => field.onChange(Number(v))}>
                  <Select.Trigger className="w-full" />
                  <Select.Content>
                    {PRIORITIES.map((priority) => (
                      <Select.Item key={priority} value={String(priority)}>
                        {Ops.Freshdesk.ticketPriorityLabel(priority)}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </FieldShell>
            )}
          />

          <Controller
            name="body"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FieldShell
                label="Message"
                hint={
                  error ? (
                    <Text size="1" color="red">
                      {error.message}
                    </Text>
                  ) : null
                }
              >
                <TextArea
                  rows={8}
                  className="w-full"
                  value={field.value}
                  onBlur={field.onBlur}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              </FieldShell>
            )}
          />
        </Flex>

        {createMutation.error ? (
          <Callout.Root color="red" size="1" mt="3">
            <Callout.Text>{createMutation.error.message}</Callout.Text>
          </Callout.Root>
        ) : null}

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray" disabled={createMutation.isPending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button
            onClick={onSubmit}
            loading={createMutation.isPending}
            disabled={createMutation.isPending || isBodyEmpty}
          >
            Send email
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
